/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

/** Minimal attribute shape used by PLP (AttributeValue) and PDP (AttributeProductExtended). */
export interface IProductBadgeAttribute {
    Name?: string | null;
    TextValue?: string | null;
    BooleanValue?: boolean | null;
}

/** Matches attribute names such as "Product Badges 1", "product badges 2", etc. */
export const PRODUCT_BADGE_ATTRIBUTE_NAME_PATTERN = /^product badges\s+\d+$/i;

/** Supported badge slugs used for CSS modifier classes. */
export type ProductBadgeType =
    | 'new'
    | 'best-seller'
    | 'coming-soon'
    | 'bundle-save'
    | 'award-winning'
    | 'clearance'
    | 'selling-fast'
    | 'low-stock'
    | 'limited-edition'
    | 'pre-order'
    | 'lux-collection'
    | 'style-collection'
    | 'on-sale';

export interface ProductBadge {
    type: ProductBadgeType;
    label: string;
}

interface SupportedBadgeDefinition {
    type: ProductBadgeType;
    label: string;
}

/** Normalized TextValue (lowercase) -> badge definition. `label` is the uppercase display text. */
const SUPPORTED_BADGE_VALUES: Readonly<Record<string, SupportedBadgeDefinition>> = {
    new: { type: 'new', label: 'NEW' },
    'best seller': { type: 'best-seller', label: 'BESTSELLER' },
    'coming soon': { type: 'coming-soon', label: 'COMING SOON' },
    'bundle & save': { type: 'bundle-save', label: 'BUNDLE & SAVE' },
    'award winning': { type: 'award-winning', label: 'AWARD-WINNING' },
    clearance: { type: 'clearance', label: 'CLEARANCE' },
    'selling fast': { type: 'selling-fast', label: 'SELLING FAST' },
    'low stock': { type: 'low-stock', label: 'LOW STOCK' },
    'limited edition': { type: 'limited-edition', label: 'LIMITED EDITION' },
    'pre order': { type: 'pre-order', label: 'PRE-ORDER' },
    'lux collection': { type: 'lux-collection', label: 'LUX COLLECTION' },
    'style collection': { type: 'style-collection', label: 'STYLE COLLECTION' },
    'on sale': { type: 'on-sale', label: 'ON SALE' }
};

function normalizeAttributeName(name?: string | null): string {
    return (name ?? '').trim().toLowerCase();
}

function normalizeBadgeTextValue(textValue?: string | null): string {
    return (textValue ?? '')
        .trim()
        .toLowerCase()
        .replace(/[-_‐-―]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function isUnavailableTextValue(textValue?: string | null): boolean {
    const normalized = normalizeBadgeTextValue(textValue);
    return normalized === '' || normalized === 'n/a' || normalized === 'na';
}

function isProductBadgeAttributeName(name?: string | null): boolean {
    return PRODUCT_BADGE_ATTRIBUTE_NAME_PATTERN.test(normalizeAttributeName(name));
}

function isAttributeUnavailable(attribute?: IProductBadgeAttribute | null): boolean {
    if (attribute == null) {
        return true;
    }

    if (attribute.BooleanValue === false) {
        return true;
    }

    return isUnavailableTextValue(attribute.TextValue);
}

function parseBadgeFromAttribute(attribute?: IProductBadgeAttribute): ProductBadge | null {
    if (isAttributeUnavailable(attribute)) {
        return null;
    }

    const normalizedText = normalizeBadgeTextValue(attribute?.TextValue);
    const definition = SUPPORTED_BADGE_VALUES[normalizedText];

    if (!definition) {
        return null;
    }

    return {
        type: definition.type,
        label: definition.label
    };
}

function sortBadgesByLabelLengthDescending(badges: ProductBadge[]): ProductBadge[] {
    return [...badges].sort((a, b) => {
        const lengthDifference = b.label.length - a.label.length;

        if (lengthDifference !== 0) {
            return lengthDifference;
        }

        return a.label.localeCompare(b.label);
    });
}

/**
 * Resolves all valid product badges from attribute values (Product Badges 1, 2, 3, ...).
 * Badges are ordered by display label length descending (longest first).
 */
export function getProductBadges(attributeValues?: IProductBadgeAttribute[] | null): ProductBadge[] {
    if (!attributeValues?.length) {
        return [];
    }

    const badges = attributeValues
        .filter(attribute => isProductBadgeAttributeName(attribute.Name))
        .map(attribute => parseBadgeFromAttribute(attribute))
        .filter((badge): badge is ProductBadge => badge !== null);

    return sortBadgesByLabelLengthDescending(badges);
}

export interface IProductBadgesOverlayProps {
    attributeValues?: IProductBadgeAttribute[] | null;
    source?: string;
}

/**
 * Renders product badge overlays. Badge order follows label character count (longest first).
 */
export const ProductBadgesOverlay: React.FC<IProductBadgesOverlayProps> = ({ attributeValues, source }) => {
    const badges = getProductBadges(attributeValues);

    if (!badges.length) {
        return null;
    }

    return (
        <div className={`${source === 'buybox' ? 'msc-product-badges--buybox' : 'msc-product-badges'}`}>
            {badges.map((badge, index) => (
                <span
                    key={`${badge.type}-${index}`}
                    className={`msc-product-badge msc-product-badge--${badge.type}`}
                >
                    {badge.label}
                </span>
            ))}
        </div>
    );
};
